import { useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { FiDownload, FiLock, FiUnlock } from "react-icons/fi";
import { useCycle } from "../../context/CycleContext";
import { useRole } from "../../context/RoleContext";
import { downloadEmployeeReport } from "../../utils/employeeReport";
import { getPerformanceReview, finalizeResult, reopenResult } from "../../services/resultApi";
import { startPipIfNeeded, findOpenPip } from "../../services/autoPipApi"; // [NEW] PM-9
import useAsync from "../../hooks/useAsync";
import { Card, PageHeader, Loader, ErrorState, Button, Banner, StatusBadge, FormField, ConfirmDialog, EvidencePanel } from "../../components/common";
import RecordAdminCard from "./components/RecordAdminCard";
import CorrectionModal from "./components/CorrectionModal";
import ScoreBreakdown from "../../components/performance/ScoreBreakdown";
import RatingScale from "../../components/performance/RatingScale";
import GoalList from "../../components/goals/GoalList";
import { KraTable, KpiTable } from "../../components/performance/KraKpiTable";
import { APPROVED_GOAL_STATUSES, RESULT_STATUS } from "../../utils/constants";
import { formatDateTime } from "../../utils/format";
import { getErrorMessage } from "../../utils/errors";

const api = { getPerformanceReview, finalizeResult, reopenResult, startPipIfNeeded, findOpenPip };

function TextBlock({ items }) {
  return (
    <div className="feedback-grid">
      {items.map(([label, text]) => (
        <div key={label}><p className="detail-label">{label}</p><p className="detail-text">{text || "—"}</p></div>
      ))}
    </div>
  );
}

export default function HrReviewDetailPage() {
  const { employeeId } = useParams();
  const { cycleId } = useCycle();
  const { profile } = useRole();
  const { data, error, reload } = useAsync(() => api.getPerformanceReview(cycleId, employeeId), [cycleId, employeeId], { skip: !cycleId });
  const [hrComments, setHrComments] = useState("");
  const [confirmFinalize, setConfirmFinalize] = useState(false);
  const [correcting, setCorrecting] = useState(false);
  const [busy, setBusy] = useState(false);

  if (error) return <ErrorState message={error} onRetry={reload} />;
  if (!data) return <Loader />;

  const { employee, result, performance, config, selfAssessment: sa, managerAssessment: ma } = data;
  const status = result.status;
  const hasApprovedGoals = data.goals.some((g) => APPROVED_GOAL_STATUSES.includes(g.status));

  const finalize = async () => {
    setBusy(true);
    try {
      await api.finalizeResult(cycleId, employeeId, { hrComments });
      toast.success(`${employee.name}'s appraisal is finalized and locked.`);
      // [UPDATED] Requirement 6: draft an improvement plan automatically when the final rating is in the PIP bands.
      // The frontend always creates it as a DRAFT (the backend is not changed); HR starts it with two reviewers.
      api.startPipIfNeeded(cycleId, employeeId)
        .then((r) => { if (r.started) toast.info(`Final rating "${r.rating}": ${employee.name} is moved to an improvement plan draft. Start it under Improvement plans (PIP) and choose the second reviewer.`); })
        .catch((err) => toast.warning(`The rating is final, but the automatic improvement plan draft could not be created: ${getErrorMessage(err)}`));
      setConfirmFinalize(false);
      reload();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const runCorrection = async ({ reason, correction }) => {
    setBusy(true);
    try {
      await api.reopenResult(cycleId, employeeId, { reason, correction });
      toast.success("Correction saved as a new revision. Release it so the employee sees it.");
      setCorrecting(false);
      reload();
      return true; // [NEW] lets CorrectionModal delete its saved draft
    } catch (err) {
      toast.error(getErrorMessage(err));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const cycleClosed = ["CLOSED", "CANCELLED"].includes(data.cycle?.backendStatus);

  return (
    <div className="page">
      <PageHeader
        backTo="/hr/review"
        backLabel="Review & finalize"
        title={employee.name}
        subtitle={`${employee.designationName}, ${employee.departmentName}. Reports to ${employee.managerName}. ${data.cycle.name}.`}
        actions={
          <>
            <StatusBadge status={status} />
            <Button
              variant="secondary"
              icon={FiDownload}
              onClick={() => {
                downloadEmployeeReport(data, { preparedBy: profile?.name });
                toast.success(`${employee.name}'s report downloaded.`);
              }}
            >
              Download report (Excel)
            </Button>
          </>
        }
      />

      {status === RESULT_STATUS.FINALIZED && (
        <Banner
          tone="success"
          title={`Finalized by ${result.finalizedByName} on ${formatDateTime(result.finalizedAt)}${result.released ? `, released ${formatDateTime(result.releasedAt)}` : ", not yet released"}${result.acknowledgedAt ? `, acknowledged ${formatDateTime(result.acknowledgedAt)}` : ""}`}
          action={cycleClosed ? null : <Button variant="ghost" size="sm" icon={FiUnlock} onClick={() => setCorrecting(true)}>Correct result</Button>}
        >
          {result.hrComments || "This appraisal is locked."}
          {cycleClosed && " The cycle is closed, so the result can no longer be corrected."}
        </Banner>
      )}
      {status === RESULT_STATUS.NOT_READY && (
        <Banner tone="neutral" title="Not ready yet">The manager hasn't submitted an assessment. Scores below are a live estimate.</Banner>
      )}

      <div className="grid-2">
        <Card>
          <ScoreBreakdown performance={performance} weights={config.weights} title="Final score" provisional={status !== RESULT_STATUS.FINALIZED} restricted={config.restricted} scoreCap={config.scoreCap} />
        </Card>
        <Card title="Rating position">
          <RatingScale score={performance.finalScore} bands={config.bands} />
          {status === RESULT_STATUS.PENDING_HR && (
            <div className="finalize-box">
              <FormField label="HR comments" hint="Stored as the finalization reason in the audit record. Not shown to the employee.">
                <textarea rows={3} value={hrComments} onChange={(e) => setHrComments(e.target.value)} placeholder="Calibration notes" />
              </FormField>
              <Button icon={FiLock} disabled={!data.stageOpen} onClick={() => setConfirmFinalize(true)}>Finalize and lock</Button>
              {!data.stageOpen && <p className="form-hint">Open the HR finalization stage to finalize.</p>}
            </div>
          )}
        </Card>
      </div>

      <RecordAdminCard data={data} cycleId={cycleId} employeeId={employeeId} onChanged={reload} />

      <Card title="Manager assessment" subtitle={ma ? `${ma.managerName}, ${ma.status}${ma.submittedAt ? ` on ${formatDateTime(ma.submittedAt)}` : ""}` : "Not started"}>
        {ma ? (
          <TextBlock items={[["Overall", ma.comments], ["Strengths", ma.strengths], ["Areas to improve", ma.improvementAreas], ["Development plan", ma.developmentPlan]]} />
        ) : <p className="muted">No manager assessment yet.</p>}
      </Card>

      <Card title="Self assessment" subtitle={sa ? `${sa.status}${sa.submittedAt ? ` on ${formatDateTime(sa.submittedAt)}` : ""}` : "Not started"}>
        {sa ? (
          <TextBlock items={[["Key achievements", sa.keyAchievements], ["Strengths", sa.strengths], ["Challenges", sa.challenges], ["Development areas", sa.developmentAreas]]} />
        ) : <p className="muted">No self assessment yet.</p>}
      </Card>

      <Card
        title="Goals"
        padded={false}
        subtitle={hasApprovedGoals && status !== RESULT_STATUS.FINALIZED ? "Approved goals change through the employee's change requests (manager decides)." : undefined}
      >
        <GoalList goals={data.goals} />
      </Card>
      <Card title="KRAs" padded={false}><KraTable rows={data.kras} /></Card>
      <Card title="KPIs" padded={false}><KpiTable rows={data.kpis} /></Card>
      <EvidencePanel owner="performance" ownerId={data.recordId} canScan={profile?.roleKeys?.includes("admin")} title="Evidence" />

      <ConfirmDialog
        open={confirmFinalize}
        title="Finalize this appraisal?"
        message={`${employee.name} will see a final score of ${performance.finalScore} (${performance.rating}). The record is locked after this.`}
        confirmLabel="Finalize"
        loading={busy}
        onConfirm={finalize}
        onClose={() => setConfirmFinalize(false)}
      />

      <CorrectionModal open={correcting} data={data} busy={busy} onClose={() => setCorrecting(false)} onSubmit={runCorrection} />
    </div>
  );
}
